import {
  ClockCircleOutlined,
  CloseOutlined,
  CrownFilled,
  EditOutlined,
  ImportOutlined,
  PlusOutlined,
  SelectOutlined,
  SwapOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { Button, Card, Empty, Flex, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import type { Allocation, Grid } from '../../DTOS/ResourcePlanning';
import CellAllocationModal, { type SelectedCell } from './CellAllocationModal';
import type { PersonRow, PlanningModel, TeamRow } from './planningModel';
import {
  allocationProjectLabel,
  cellAction,
  colorOf,
  hours,
  IconButton,
  isWeekend,
  loadColor,
  membersOn,
  planningTeamId,
  slotText,
  UNASSIGNED,
  useCanEdit,
} from './shared';

/** Prefill for a new allocation; `to` is set when a period was selected (drag in the Gantt). */
export interface CellTarget {
  teamId?: string | null;
  userId?: string;
  projectId?: string;
  date: string;
  to?: string;
  minutes?: number;
}
export interface ViewProps {
  model: PlanningModel;
  loading?: boolean;
  onDetails: (allocations: Allocation[]) => void;
  onCreate: (target: CellTarget) => void;
  onDay: (target: CellTarget) => void;
  /** Opens the swap between two technicians, prefilled with this person. */
  onSwap?: (person: { userId: string; name: string }) => void;
}
interface Row {
  key: string;
  team: TeamRow;
  person?: PersonRow;
  children?: Row[];
}
export const WEEKDAYS = ['Du', 'Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sa'];
const sum = (allocations: Allocation[]) => allocations.reduce((total, allocation) => total + allocation.minutes, 0);
const short = (minutes: number) => `${+(minutes / 60).toFixed(1)}`;
const percent = (minutes: number, capacity: number) => (capacity ? Math.round((minutes * 100) / capacity) : null);

export const PersonLabel: React.FC<{ person: PersonRow; onSwap?: ViewProps['onSwap'] }> = ({ person, onSwap }) => (
  <span>
    {person.isLeader && (
      <Tooltip title='Sef echipa'>
        <CrownFilled style={{ color: '#faad14', marginRight: 4 }} />
      </Tooltip>
    )}
    <Typography.Text delete={!person.isActive} type={person.isActive ? undefined : 'secondary'}>
      {person.name}
    </Typography.Text>
    {!person.isMember && (
      <Tooltip title='Imprumutat din alta echipa'>
        <ImportOutlined style={{ marginLeft: 6, color: '#8c8c8c' }} />
      </Tooltip>
    )}
    {onSwap && (
      <IconButton
        title='Schimb cu alt tehnician'
        size='small'
        type='text'
        icon={<SwapOutlined style={{ color: '#722ed1' }} />}
        style={{ marginLeft: 2 }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={() => onSwap({ userId: person.userId, name: person.name })}
      />
    )}
  </span>
);
interface Position {
  row: number;
  col: number;
}
const between = (value: number, a: number, b: number) => value >= Math.min(a, b) && value <= Math.max(a, b);

/**
 * Team and person rows by day. Drag across cells, Ctrl+click or Shift+click to select several technician/team days,
 * then allocate a program to all of them or edit the work already in the selection.
 */
const PlanningTable: React.FC<ViewProps & { grid?: Grid }> = ({ model, grid, loading, onDetails, onCreate, onDay, onSwap }) => {
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<Position | null>(null);
  const [drag, setDrag] = useState<{ start: Position; end: Position } | null>(null);
  const [allocating, setAllocating] = useState<SelectedCell[] | null>(null);
  const canEdit = useCanEdit();
  const { dates, rows, loads } = model;
  const compact = dates.length > 7;
  const width = dates.length > 31 ? 52 : compact ? 64 : 210;
  const labelOf = (allocation: Allocation) =>
    allocationProjectLabel(allocation, model.projectsById.get(allocation.projectId));
  const data: Row[] = rows.map((team) => ({
    key: team.key,
    team,
    children: team.people.map((person) => ({ key: person.key, team, person })),
  }));
  const of = (row: Row, date?: string) =>
    (row.person ? row.person.allocations : row.team.allocations).filter(
      (allocation) => !date || allocation.date === date,
    );

  // Rows in display order, so a dragged or Shift+click rectangle covers what the user sees.
  const order = data.flatMap((row) => [row, ...(collapsed.includes(row.key) ? [] : row.children || [])]);
  const rowIndex = new Map(order.map((row, position) => [row.key, position]));
  const dateIndex = new Map(dates.map((date, position) => [date, position]));
  const keyOf = (row: Row, date: string) => `${row.key}|${date}`;
  const rectangle = (a: Position, b: Position) =>
    new Set(
      order
        .filter((_, position) => between(position, a.row, b.row))
        .flatMap((row) => dates.filter((_, col) => between(col, a.col, b.col)).map((date) => keyOf(row, date))),
    );
  const dragging = !!drag && (drag.start.row !== drag.end.row || drag.start.col !== drag.end.col);
  const isSelected = (row: Row, date: string) =>
    dragging
      ? between(rowIndex.get(row.key)!, drag!.start.row, drag!.end.row) &&
        between(dateIndex.get(date)!, drag!.start.col, drag!.end.col)
      : selected.has(keyOf(row, date));
  const clear = () => {
    setSelected(new Set());
    setAnchor(null);
  };
  useEffect(clear, [dates[0], dates[dates.length - 1]]);
  useEffect(() => {
    if (!drag) return undefined;
    const finish = () => {
      if (drag.start.row !== drag.end.row || drag.start.col !== drag.end.col) {
        setSelected(rectangle(drag.start, drag.end));
        setAnchor(drag.start);
      }
      setDrag(null);
    };
    window.addEventListener('mouseup', finish);
    return () => window.removeEventListener('mouseup', finish);
  }, [drag]);
  useEffect(() => {
    if (!selected.size) return undefined;
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') clear();
    };
    window.addEventListener('keydown', escape);
    return () => window.removeEventListener('keydown', escape);
  }, [selected.size]);
  /** Mouse selection on a cell; a plain click keeps the cell's own action. */
  const selectable = (row: Row, date: string, action: () => void) => {
    const position = { row: rowIndex.get(row.key)!, col: dateIndex.get(date)! };
    return {
      onMouseDown: (event: React.MouseEvent) => {
        if (event.button === 0 && !event.shiftKey && !event.ctrlKey && !event.metaKey)
          setDrag({ start: position, end: position });
      },
      onMouseEnter: () => {
        if (drag && (drag.end.row !== position.row || drag.end.col !== position.col)) setDrag({ ...drag, end: position });
      },
      onClick: (event: React.MouseEvent) => {
        if (event.ctrlKey || event.metaKey) {
          const next = new Set(selected),
            key = keyOf(row, date);
          if (!next.delete(key)) next.add(key);
          setSelected(next);
          setAnchor(position);
        } else if (event.shiftKey) {
          setSelected(rectangle(anchor || position, position));
          if (!anchor) setAnchor(position);
        } else if (selected.size) clear();
        else action();
      },
    };
  };
  const selectedStyle = (row: Row, date: string): React.CSSProperties =>
    isSelected(row, date) ? { boxShadow: 'inset 0 0 0 2px #1677ff', backgroundColor: 'rgba(22,119,255,0.12)' } : {};

  // A team cell selects the whole team that day (active home members); the direct row selects its people.
  const people = new Map((grid?.people || []).map((person) => [person.id, person]));
  const selection = order.flatMap((row) =>
    dates.filter((date) => selected.has(keyOf(row, date))).map((date) => ({ row, date })),
  );
  const cells = new Map<string, SelectedCell>();
  // Person cells first, so their planning team wins over a team cell covering the same person and day.
  selection.forEach(({ row, date }) => {
    if (row.person)
      cells.set(`${row.person.userId}:${date}`, {
        userId: row.person.userId,
        name: row.person.name,
        teamId: planningTeamId(row.team.key),
        date,
      });
  });
  selection.forEach(({ row, date }) => {
    if (row.person) return;
    const team =
      row.team.key === UNASSIGNED
        ? row.team.people.map((person) => ({ userId: person.userId, name: person.name }))
        : membersOn(row.team.team, date)
            .filter((member) => people.get(member.userId)?.isActive !== false)
            .map((member) => ({ userId: member.userId, name: member.userName }));
    team.forEach((person) => {
      if (!cells.has(`${person.userId}:${date}`))
        cells.set(`${person.userId}:${date}`, { ...person, teamId: planningTeamId(row.team.key), date });
    });
  });
  const inSelection = [
    ...new Map(
      selection.flatMap(({ row, date }) => of(row, date)).map((allocation) => [allocation.id, allocation]),
    ).values(),
  ];

  const personCell = (row: Row, date: string) => {
    const person = row.person!,
      allocations = of(row, date),
      load = loads.get(`${person.userId}:${date}`);
    const open = () => onDay({ teamId: row.team.key, userId: person.userId, date });
    const day = { ...cellAction(`${person.name} ${date}: planificare zilnica`, open), ...selectable(row, date, open) };
    if (!allocations.length)
      return (
        <div
          {...day}
          title='Click: deschide ziua · trageti sau Ctrl/Shift + click: selectie multipla'
          style={{ minHeight: compact ? 26 : 38, cursor: 'copy', borderRadius: 4, ...selectedStyle(row, date) }}
        />
      );
    const tooltip = (
      <>
        {allocations.map((allocation) => (
          <div key={allocation.id}>
            {labelOf(allocation)} · {hours(allocation.minutes)}
          </div>
        ))}
        {load && (
          <div style={{ marginTop: 4 }}>
            Total zi: {hours(load.minutes)} / {hours(load.capacity)}
          </div>
        )}
      </>
    );
    const style: React.CSSProperties = {
      background: load ? loadColor(load.minutes, load.capacity) : undefined,
      borderRadius: 4,
      cursor: 'pointer',
      ...selectedStyle(row, date),
    };
    if (compact)
      return (
        <Tooltip title={tooltip}>
          <div
            {...day}
            style={{
              ...style,
              minHeight: 26,
              lineHeight: '26px',
              textAlign: 'center',
              fontSize: 12,
              borderLeft: `3px solid ${colorOf(allocations[0].projectId)}`,
            }}
          >
            {short(sum(allocations))}
            {load?.overloaded && <WarningFilled style={{ color: '#cf1322', marginLeft: 2 }} />}
          </div>
        </Tooltip>
      );
    return (
      <div {...day} style={{ ...style, padding: 4, minHeight: 38 }}>
        {allocations.map((allocation) => (
          <Tooltip key={allocation.id} title={labelOf(allocation)}>
            <div
              style={{
                borderLeft: `3px solid ${colorOf(allocation.projectId)}`,
                paddingLeft: 4,
                fontSize: 12,
                marginBottom: 4,
                lineHeight: 1.4,
                overflowWrap: 'anywhere',
              }}
            >
              <div>{labelOf(allocation)}</div>
              <strong>{hours(allocation.minutes)}</strong>
              {allocation.startTime && <div style={{ color: '#595959', fontSize: 11 }}>{slotText(allocation)}</div>}
            </div>
          </Tooltip>
        ))}
        {load && (
          <Typography.Text type={load.overloaded ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
            {load.overloaded && <WarningFilled />} {hours(load.minutes)} / {hours(load.capacity)}
          </Typography.Text>
        )}
      </div>
    );
  };
  const teamCell = (row: Row, date: string) => {
    const total = sum(of(row, date)),
      capacity = row.team.capacity(date);
    // An empty team day allocates the whole team on that day.
    const create = () => onCreate({ teamId: row.team.key === UNASSIGNED ? null : row.team.key, date });
    const open = () => (total ? onDetails(of(row, date)) : create());
    if (!total && !capacity)
      return (
        <div
          {...cellAction(`${row.team.team.name} ${date}: aloca echipa`, create)}
          {...selectable(row, date, create)}
          title='Click pentru alocarea echipei'
          style={{ minHeight: 24, cursor: 'copy', borderRadius: 4, ...selectedStyle(row, date) }}
        />
      );
    return (
      <Tooltip
        title={
          total
            ? `${hours(total)} alocate din ${hours(capacity)} disponibile`
            : `${hours(capacity)} disponibile · click pentru alocarea echipei`
        }
      >
        <div
          {...cellAction(`${row.team.team.name} ${date}: planificare echipa`, open)}
          {...selectable(row, date, open)}
          style={{
            background: loadColor(total, capacity),
            ...selectedStyle(row, date),
            borderRadius: 4,
            textAlign: 'center',
            fontSize: 12,
            fontWeight: 600,
            cursor: total ? 'pointer' : 'copy',
            lineHeight: '24px',
          }}
        >
          {compact ? short(total) : `${hours(total)} / ${hours(capacity)}`}
        </div>
      </Tooltip>
    );
  };
  const columns: ColumnsType<Row> = [
    {
      title: 'Echipa / persoana',
      key: 'name',
      fixed: 'left',
      width: 240,
      render: (_, row) =>
        row.person ? (
          <PersonLabel person={row.person} onSwap={onSwap} />
        ) : (
          <Space size={4} wrap>
            <strong>{row.team.team.name}</strong>
            <Tooltip title='Program zilnic'>
              <Tag bordered={false} icon={<ClockCircleOutlined />}>
                {hours(row.team.team.dailyMinutes)}
              </Tag>
            </Tooltip>
          </Space>
        ),
    },
    ...dates.map((date) => ({
      title: (
        <div
          style={{
            textAlign: 'center',
            lineHeight: 1.2,
            fontSize: compact ? 11 : undefined,
            color: isWeekend(date) ? '#bfbfbf' : undefined,
          }}
        >
          {WEEKDAYS[dayjs(date).day()]}
          <br />
          {dayjs(date).format(compact ? 'DD' : 'DD.MM')}
        </div>
      ),
      key: date,
      width,
      onCell: () => ({
        style: { padding: 2, background: isWeekend(date) ? '#fafafa' : undefined, userSelect: 'none' as const },
      }),
      render: (_: unknown, row: Row) => (row.person ? personCell(row, date) : teamCell(row, date)),
    })),
    {
      title: 'Total',
      key: 'total',
      fixed: 'right',
      width: 120,
      render: (_, row) => {
        const allocations = of(row),
          total = sum(allocations);
        const capacity = dates.reduce(
          (all, date) => all + (row.person ? row.person.capacity(date) : row.team.capacity(date)),
          0,
        );
        const usage = percent(total, capacity);
        return (
          <Tooltip title={`${hours(total)} alocate din ${hours(capacity)} disponibile`}>
            <Typography.Link strong={!row.person} onClick={() => allocations.length && onDetails(allocations)}>
              {hours(total)}
            </Typography.Link>
            {usage !== null && (
              <Typography.Text type={usage > 100 ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                {' '}
                · {usage}%
              </Typography.Text>
            )}
          </Tooltip>
        );
      },
    },
  ];
  // A team lead acts only on the members of the team they lead; other selected days stay read-only.
  const editableCells = [...cells.values()].filter((cell) => canEdit(cell.userId, cell.date));
  const editableWork = inSelection.filter((allocation) => canEdit(allocation.userId, allocation.date));
  const readOnlyDays = cells.size - editableCells.length;
  const selectedPeople = new Set([...cells.values()].map((cell) => cell.userId)).size;
  const selectedDays = new Set([...cells.values()].map((cell) => cell.date)).size;
  return (
    <div>
      <Table<Row>
      rowKey='key'
      loading={loading}
      dataSource={data}
      columns={columns}
      size='small'
      pagination={false}
      bordered
      sticky
      scroll={{ x: 360 + width * dates.length }}
      expandable={{
        expandedRowKeys: data.filter((row) => !collapsed.includes(row.key)).map((row) => row.key),
        onExpand: (expanded, row) =>
          setCollapsed((current) => (expanded ? current.filter((key) => key !== row.key) : [...current, row.key])),
      }}
      locale={{
        emptyText: (
          <Empty description='Nicio persoana pentru filtrele selectate. Adaugati echipe si membri pentru a incepe planificarea.' />
        ),
      }}
      />
      {selection.length > 0 ? (
        // Sticky at the bottom of the viewport while the table is in view.
        <Card
          size='small'
          style={{ position: 'sticky', bottom: 12, zIndex: 5, marginTop: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
        >
          <Flex wrap gap={8} align='center' justify='space-between'>
            <Space wrap>
              <SelectOutlined style={{ color: '#1677ff' }} />
              <strong>{cells.size} zile selectate</strong>
              <Typography.Text type='secondary'>
                {selectedPeople} tehnicieni · {selectedDays} date
                {inSelection.length > 0 && ` · ${hours(sum(inSelection))} deja alocate`}
                {readOnlyDays > 0 && ` · ${readOnlyDays} doar vizualizare`}
              </Typography.Text>
            </Space>
            <Space wrap>
              <Button
                type='primary'
                icon={<PlusOutlined />}
                disabled={!editableCells.length || !grid}
                onClick={() => setAllocating(editableCells)}
              >
                Aloca program
              </Button>
              {editableWork.length > 0 && (
                <Button icon={<EditOutlined />} onClick={() => onDetails(editableWork)}>
                  Alocarile existente ({editableWork.length})
                </Button>
              )}
              <IconButton title='Goleste selectia (Esc)' icon={<CloseOutlined />} onClick={clear} />
            </Space>
          </Flex>
        </Card>
      ) : (
        <Typography.Text type='secondary' style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          Selectie multipla: trageti peste celule, Ctrl + click pentru zile separate, Shift + click pentru un interval. O
          celula de echipa selecteaza toti membrii echipei din acea zi.
        </Typography.Text>
      )}
      {allocating && grid && (
        <CellAllocationModal
          cells={allocating}
          grid={grid}
          onClose={() => setAllocating(null)}
          onSaved={() => {
            setAllocating(null);
            clear();
          }}
        />
      )}
    </div>
  );
};
export default PlanningTable;
